import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CultivoItemViewModel, CultivoViewModel, TwCultivoViewModel, TwRecintoCultivosViewModel } from './cultivo.types';
import { DatePipe } from '@angular/common';
import { FilterSearchingTWRecintoViewModel, RecintoItemResultsViewModel, RecintoPIAEdListViewModel } from '../recintos/recinto.types';
import { EnumSiNoTodos } from '../../core/_infrastructure/types/common';

@Injectable()
export class CultivoService {
    private endpoint = this.baseUrl + 'api/campanias/cultivo/';
    private arrBooleanKeys = ['EsCompleta', 'Autorizado'];

    constructor(
        private datePipe: DatePipe,
        private http: HttpClient,
        @Inject('BASE_URL') private baseUrl: string,
    ) {}

    /**
     * Invokes the service to get the "Cultivo"
     * @param id: number
     * @returns Observable<CultivoViewModel>
     */
    getCultivo(id: number): Observable<CultivoViewModel> {
        return this.http.get<CultivoViewModel>(this.endpoint + id);
    }

    /**
     * Invokes the service to get the Recinto and its "Cultivos"
     * @param idRecinto: number
     * @returns Observable<TwRecintoCultivosViewModel>
     */
    getRecintoCultivos(idRecinto: number): Observable<TwRecintoCultivosViewModel> {
        return this.http.get<TwRecintoCultivosViewModel>(this.endpoint + 'ByRecinto/' + idRecinto);
    }

    /**
     * Invokes the service to get all the "Cultivos" by 'IdRecinto'
     * @param idRecinto: number
     * @returns Observable<TwRecintoCultivosViewModel>
     */
    getAllCultivosByIdRecinto(idRecinto: number): Observable<CultivoViewModel[]> {
        return this.http.get<CultivoViewModel[]>(this.endpoint + 'AllCultivosByIdRecinto/' + idRecinto);
    }

    save(viewModel: TwCultivoViewModel): Observable<any> {
        // delete viewModel.Descripcion;  // Para forzar un fallo en el backend y ver que pasa en el catchError
        if (viewModel?.Id) {
            return this.edit(viewModel);
        }
        return this.create(viewModel);
    }

    /**
     * Se da de baja (archiva) el cultivo indicado por el id.
     * Las hectáreas ocupadas por ése cultivo se liberan.
     * El cultivo no podrá estar vinculado a una solicitud/UI en periodo vigente.
     * @param id number
     * @returns Observable<any>
     */
    baja(id: number): Observable<any> {
        return this.http.put<any>(this.endpoint + 'baja/' + id, null);
    }

    private create(viewModel: TwCultivoViewModel): Observable<any> {
        // viewModel.Id = 1121; // Para forzar un fallo en el backend y ver que pasa en el catchError
        return this.http.post<TwCultivoViewModel>(this.endpoint, viewModel);
    }

    private edit(viewModel: TwCultivoViewModel): Observable<any> {
        // viewModel.Id = null; // Para forzar un fallo en el backend y ver que pasa en el catchError
        return this.http.put<TwCultivoViewModel>(this.endpoint, viewModel);
    }

    delete(id: number): Observable<any> {
        return this.http.delete<any>(this.endpoint + id);
    }

    /**
     * Calcula la suma de la superficie ocupada por todos los cultivos en un recinto, sin tener en cuenta la superficie del cultivo pasado por parámetro.
     * Los cultivos a tener en cuenta para el sumatorio no deberán tener fecha de baja.
     * @param cultivos CultivoItemViewModel[]
     * @param cultivoId number Parámetro opcional
     * @returns number
     */
    getSuperficieCultivada(cultivos: CultivoItemViewModel[], cultivoId = -1): number {
        let superficieCultivada = 0;
        cultivos?.forEach(cultivo => {
            if (cultivo.Id != cultivoId && this.checkFechaBaja(cultivo.FechaBaja) === undefined) {
                superficieCultivada += cultivo.SuperficieVariedad;
            }
        });
        return superficieCultivada;
    }

    validateSuperficieCultivada(superficieVariedad: number, superficieCultivada: number, superficieTotal: number): boolean {
        return superficieVariedad + superficieCultivada <= superficieTotal;
    }

    /**
     * Un cultivo está dado de baja si el campo FechaBaja es una fecha distinta de '01/01/0001'
     * @param fecha Date
     * @returns Date
     */
    checkFechaBaja(fecha: Date | undefined): Date | undefined {
        let formatToUse = 'dd/MM/yyyy';

        const parsedDate = this.datePipe.transform(fecha, formatToUse);

        if (parsedDate && parsedDate !== '01/01/0001') {
            return fecha;
        }
        return undefined;
    }

    /**
     * Creates an HttpParms object through the values in the parameter.
     * @param payload FilterSearchingTWRecintoViewModel
     * @returns HttpParams
     */
    private convertModelToHttpParms(payload: FilterSearchingTWRecintoViewModel): HttpParams {
        let params = new HttpParams();
        let k: keyof FilterSearchingTWRecintoViewModel;

        for (k in payload) {
            let v;
            let valor = payload[k];
            if (this.arrBooleanKeys.indexOf(k) !== -1 && valor == EnumSiNoTodos.NO) {
                v = 0;
            } else {
                v = payload[k] || '';
            }
            params = params.append(k, v);
        }

        return params;
    }

    /**
     * Invokes the service to get the list of "RecintosCultivos", applying the filter in the parameter.
     * Este método solo debería invocarse desde la selección de cultivos para la solicitud.
     * @param payload FilterSearchingTWRecintoViewModel
     * @returns Observable<RecintoItemResultsViewModel>
     */
    getRecintosCultivos(payload: FilterSearchingTWRecintoViewModel): Observable<RecintoItemResultsViewModel> {
        const params = this.convertModelToHttpParms(payload);
        return this.getRecintosCultivosPagedByFilter(params);
    }

    /**
     * Invokes the service to get the list of "RecintosCultivos", applying the filter in the parameter
     * @param params HttpParams
     * @returns Observable<RecintoItemResultsViewModel>
     */
    private getRecintosCultivosPagedByFilter(params: HttpParams): Observable<RecintoItemResultsViewModel> {
        return this.http.get<RecintoItemResultsViewModel>(this.endpoint + 'PagedByFilter/', { params: params });
    }

    getCultivosByList(payload: RecintoPIAEdListViewModel): Observable<RecintoItemResultsViewModel> {
        return this.http.post<any>(this.endpoint + 'byList/', payload);
    }
}
